import { lazy, Suspense } from "react";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { workTranslationOf } from "@/lib/medical-schema";
import { createFileRoute } from "@tanstack/react-router";
import { ORG_REF } from "@/lib/medical-schema";
import { Hero, heroPreloadLink } from "@/components/site/Hero";
import { AboutDoctor } from "@/components/site/AboutDoctor";

// Below-the-fold sections are code-split so the initial view ships a smaller bundle.
// They still render during SSR, so the HTML (and SEO content) is unchanged.
const ServicesGrid = lazy(() =>
  import("@/components/site/ServicesGrid").then((m) => ({ default: m.ServicesGrid })),
);
const ResultsSection = lazy(() =>
  import("@/components/site/ResultsSection").then((m) => ({ default: m.ResultsSection })),
);
const WhyUs = lazy(() => import("@/components/site/WhyUs").then((m) => ({ default: m.WhyUs })));
const WebinarCTA = lazy(() =>
  import("@/components/site/WebinarCTA").then((m) => ({ default: m.WebinarCTA })),
);

function SectionFallback() {
  return <div aria-hidden="true" className="min-h-[60vh]" />;
}


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Bariatric Surgery & Medical Weight Loss | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Board-certified bariatric surgery and weight loss care from Dr. Fitzer: gastric sleeve, bypass, ESG, and GLP-1 medications in Northern Virginia.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Bariatric Surgery & Medical Weight Loss | Virginia Bariatric" },
        { property: "og:description", content: "Board-certified bariatric surgery and weight loss care from Dr. Fitzer: gastric sleeve, bypass, ESG, and GLP-1 medications in Northern Virginia." },
        { property: "og:url", content: "https://virginiabariatric.com/" },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { property: "og:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { property: "og:image:secure_url", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: "Bariatric surgery and weight loss practice in Northern Virginia — Dr. Matthew Fitzer, board-certified bariatric surgeon" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { name: "twitter:image:alt", content: "Bariatric surgery and weight loss practice in Northern Virginia — Dr. Matthew Fitzer, board-certified bariatric surgeon" },
        { name: "twitter:title", content: "Bariatric Surgery & Medical Weight Loss | Virginia Bariatric" },
        { name: "twitter:description", content: "Board-certified bariatric surgery and weight loss care from Dr. Fitzer: gastric sleeve, bypass, ESG, and GLP-1 medications in Northern Virginia." },
      ] : []),
    ],
    links: [
      heroPreloadLink,
      ...(import.meta.env.SSR ? canonicalAndAlternates("/") : []),
    ],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            // The MedicalClinic/Organization, Person, and WebSite nodes are
            // emitted once sitewide from src/routes/__root.tsx. Repeating them
            // here produced two nodes per @id, which validators read as
            // conflicting entity definitions, so this graph carries only the
            // page-specific nodes.
            {
              "@type": "ImageObject",
              "@id": "https://virginiabariatric.com/#primaryimage",
              contentUrl: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg",
              url: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg",
              width: 1280,
              height: 853,
              caption: "Achieve the lifestyle you've always wanted",
              representativeOfPage: true,
            },

            {
              "@type": "WebPage",
              "@id": "https://virginiabariatric.com/#webpage",
              ...workTranslationOf("/"),
              url: "https://virginiabariatric.com/",
              name: "Board-Certified Bariatric Surgery in Northern Virginia | Dr. Fitzer",
              description:
                "Board-certified bariatric surgeon Dr. Fitzer has performed 6,000+ weight-loss operations: sleeve, bypass, and GLP-1 care in Northern Virginia.",
              inLanguage: "en-US",
              isPartOf: { "@id": "https://virginiabariatric.com/#website" },
              about: ORG_REF,
              primaryImageOfPage: { "@id": "https://virginiabariatric.com/#primaryimage" },
              hasPart: { "@id": "https://virginiabariatric.com/#sitenav" },
            },
            {
              "@type": "ItemList",
              "@id": "https://virginiabariatric.com/#sitenav",
              name: "Virginia Bariatric Surgery main sections",
              itemListElement: [
                { name: "Bariatric Surgery", url: "https://virginiabariatric.com/services" },
                { name: "Medical Weight Loss", url: "https://virginiabariatric.com/medical-weight-loss" },
                { name: "Our Program", url: "https://virginiabariatric.com/our-program" },
                { name: "Before & After", url: "https://virginiabariatric.com/results" },
                { name: "Resources", url: "https://virginiabariatric.com/resources" },
                { name: "Appointments", url: "https://virginiabariatric.com/appointments" },
              ].map((item, index) => ({
                "@type": "SiteNavigationElement",
                position: index + 1,
                name: item.name,
                url: item.url,
              })),
            },
          ],

        }),
      },
    ] : [],
  }),
  component: Home,
});


function Home() {
  return (
    <>
      <Hero />
      <AboutDoctor />
      <Suspense fallback={<SectionFallback />}>
        <ServicesGrid />
        <ResultsSection />
        <WhyUs />
        <WebinarCTA />
      </Suspense>

    </>
  );
}
